import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import { PaymentMethod, CardDetails, MobilePaymentDetails, BankTransferDetails } from '../types/payment.types';

interface PaymentFormProps {
  paymentMethod: PaymentMethod;
  onSubmit: (details: any) => void;
  onBack: () => void;
  loading: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  paymentMethod,
  onSubmit,
  onBack,
  loading,
}) => {
  const [cardDetails, setCardDetails] = useState<CardDetails>({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardHolderName: '',
  });

  const [mobileDetails, setMobileDetails] = useState<MobilePaymentDetails>({
    phoneNumber: '',
    provider: 'mtn',
  });

  const [bankDetails, setBankDetails] = useState<BankTransferDetails>({
    bankCode: '',
    accountNumber: '',
    accountName: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let details;
    switch (paymentMethod.type) {
      case 'card':
        details = cardDetails;
        break;
      case 'mobile':
        details = mobileDetails;
        break;
      case 'bank':
        details = bankDetails;
        break;
      default:
        return;
    }
    
    onSubmit(details);
  };

  const renderCardForm = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Informations de la carte
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Nom du titulaire de la carte"
            value={cardDetails.cardHolderName}
            onChange={(e) => setCardDetails({ ...cardDetails, cardHolderName: e.target.value })}
            required
          />
          
          <TextField
            fullWidth
            label="Numéro de la carte"
            value={cardDetails.cardNumber}
            onChange={(e) => setCardDetails({ ...cardDetails, cardNumber: e.target.value })}
            placeholder="1234 5678 9012 3456"
            required
          />
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Mois</InputLabel>
              <Select
                value={cardDetails.expiryMonth}
                label="Mois"
                onChange={(e) => setCardDetails({ ...cardDetails, expiryMonth: e.target.value })}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                    {String(i + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Année</InputLabel>
              <Select
                value={cardDetails.expiryYear}
                label="Année"
                onChange={(e) => setCardDetails({ ...cardDetails, expiryYear: e.target.value })}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Code de sécurité"
              value={cardDetails.cvv}
              onChange={(e) => setCardDetails({ ...cardDetails, cvv: e.target.value })}
              placeholder="123"
              required
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderMobileForm = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Informations de la transaction mobile money
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Provider</InputLabel>
            <Select
              value={mobileDetails.provider}
              label="Provider"
              onChange={(e) => setMobileDetails({ ...mobileDetails, provider: e.target.value as any })}
            >
              <MenuItem value="mtn">MTN Mobile Money</MenuItem>
              <MenuItem value="orange">Orange Money</MenuItem>
              <MenuItem value="moov">Moov Money</MenuItem>
              <MenuItem value="wave">Wave</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Numéro de téléphone"
            value={mobileDetails.phoneNumber}
            onChange={(e) => setMobileDetails({ ...mobileDetails, phoneNumber: e.target.value })}
            placeholder="0700000000"
            required
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderBankForm = () => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Informations de la transaction bancaire
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Bank Code"
            value={bankDetails.bankCode}
            onChange={(e) => setBankDetails({ ...bankDetails, bankCode: e.target.value })}
            placeholder="001"
            required
          />
          
          <TextField
            fullWidth
            label="Account Number"
            value={bankDetails.accountNumber}
            onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
            placeholder="1234567890"
            required
          />
          
          <TextField
            fullWidth
            label="Account Name"
            value={bankDetails.accountName}
            onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
            placeholder="John Doe"
            required
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderForm = () => {
    switch (paymentMethod.type) {
      case 'card':
        return renderCardForm();
      case 'mobile':
        return renderMobileForm();
      case 'bank':
        return renderBankForm();
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {paymentMethod.name} Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your {paymentMethod.name.toLowerCase()} information to complete the payment
      </Typography>

      <form onSubmit={handleSubmit}>
        {renderForm()}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={onBack} disabled={loading} color="primary">
            Retour
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PaymentForm; 